import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { Dumbbell, Plus, Loader2, Search, X, Trash2, Edit, Globe, User, Upload, Video, Play, Trash } from 'lucide-react';
import { needsConversion, convertToMp4 } from '../../utils/videoConverter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Pecho', 'Espalda', 'Hombros', 'B\u00edceps', 'Tr\u00edceps', 'Cu\u00e1driceps',
  'Isquiotibiales', 'Gl\u00fateos', 'Aductores', 'Abductores', 'Gemelos', 'Core',
  'Antebrazos', 'TRX/Suspensi\u00f3n', 'Propiocepci\u00f3n', 'Yoga', 'Otros'
];

const TrainerExercisesPage = () => {
  const [data, setData] = useState({ global: [], my_exercises: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newExercise, setNewExercise] = useState({ name: '', category: 'Otros', muscle_group: '', equipment: '', technical_notes: '' });

  // ---- Video upload state (per exercise) ----
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [convertingVideo, setConvertingVideo] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingExerciseId, setPendingExerciseId] = useState(null);

  // Player modal
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerUrl, setPlayerUrl] = useState(null);

  useEffect(() => { fetchExercises(); }, []);

  const fetchExercises = async () => {
    try {
      const res = await axios.get(`${API}/trainer/exercises`);
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar ejercicios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newExercise.name) { toast.error('El nombre es obligatorio'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/trainer/exercises`, newExercise);
      toast.success('Ejercicio creado');
      setShowCreate(false);
      setNewExercise({ name: '', category: 'Otros', muscle_group: '', equipment: '', technical_notes: '' });
      fetchExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!showEdit) return;
    try {
      await axios.put(`${API}/trainer/exercises/${showEdit.id}`, showEdit);
      toast.success('Ejercicio actualizado');
      setShowEdit(null);
      fetchExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este ejercicio?')) return;
    try {
      await axios.delete(`${API}/trainer/exercises/${id}`);
      toast.success('Ejercicio eliminado');
      fetchExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  // ---- Video upload (chunked) ----
  const triggerUploadFor = (exerciseId) => {
    setPendingExerciseId(exerciseId);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !pendingExerciseId) return;

    const exerciseId = pendingExerciseId;
    setPendingExerciseId(null);
    setUploadingId(exerciseId);
    setUploadProgress(0);

    let fileToUpload = file;
    try {
      if (await needsConversion(file)) {
        setConvertingVideo(true);
        toast.info('Convirtiendo video a H.264...');
        fileToUpload = await convertToMp4(file, () => {});
        setConvertingVideo(false);
      }
    } catch (err) {
      console.error('client-side conversion failed', err);
      setConvertingVideo(false);
      fileToUpload = file;
    }

    try {
      const CHUNK_SIZE = 1.5 * 1024 * 1024;
      const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);
      const uploadId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
        const chunk = fileToUpload.slice(start, end);
        const fd = new FormData();
        fd.append('chunk', chunk);
        fd.append('chunk_index', i.toString());
        fd.append('total_chunks', totalChunks.toString());
        fd.append('upload_id', uploadId);
        fd.append('filename', fileToUpload.name);
        await axios.post(`${API}/trainer/exercises/${exerciseId}/upload-video-chunk`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
      }
      const res = await axios.post(`${API}/trainer/exercises/${exerciseId}/finalize-video-upload`, {
        upload_id: uploadId, filename: fileToUpload.name, total_chunks: totalChunks
      }, { timeout: 180000 });
      setUploadProgress(100);
      toast.success(`Video subido: ${res.data.file_size_mb || '?'}MB · Propagado a ${res.data.propagated_to_plans || 0} planes`);
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al subir el video');
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const handleDeleteVideo = async (exerciseId) => {
    if (!window.confirm('¿Eliminar el video de este ejercicio?')) return;
    try {
      await axios.delete(`${API}/trainer/exercises/${exerciseId}/video`);
      toast.success('Video eliminado');
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openPlayer = (videoUrl) => {
    if (!videoUrl) return;
    const fullUrl = videoUrl.startsWith('http') ? videoUrl : `${process.env.REACT_APP_BACKEND_URL}${videoUrl}`;
    setPlayerUrl(fullUrl);
    setPlayerOpen(true);
  };

  const filteredGlobal = useMemo(() => {
    return (data.global || []).filter(ex => {
      const matchSearch = !searchTerm || ex.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = selectedCategory === 'all' || ex.category === selectedCategory;
      return matchSearch && matchCat;
    });
  }, [data.global, searchTerm, selectedCategory]);

  const filteredMine = useMemo(() => {
    return (data.my_exercises || []).filter(ex => {
      const matchSearch = !searchTerm || ex.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = selectedCategory === 'all' || ex.category === selectedCategory;
      return matchSearch && matchCat;
    });
  }, [data.my_exercises, searchTerm, selectedCategory]);

  if (loading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  return (
    <TrainerLayout>
      <div className="space-y-6" data-testid="trainer-exercises">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">Ejercicios</h1>
          <Button onClick={() => setShowCreate(true)} data-testid="create-exercise-btn">
            <Plus className="h-4 w-4 mr-2" /> Crear Ejercicio
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar ejercicio..." className="pl-9 bg-zinc-900/50 border-zinc-800" />
            {searchTerm && (
              <Button variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchTerm('')}><X className="h-3 w-3" /></Button>
            )}
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="global">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="global" className="flex items-center gap-2">
              <Globe className="h-4 w-4" /> Biblioteca Global ({filteredGlobal.length})
            </TabsTrigger>
            <TabsTrigger value="mine" className="flex items-center gap-2">
              <User className="h-4 w-4" /> Mis Ejercicios ({filteredMine.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="global" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredGlobal.map(ex => (
                <Card key={ex.id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground text-sm">{ex.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{ex.category}</Badge>
                          {ex.muscle_group && <span className="text-xs text-muted-foreground">{ex.muscle_group}</span>}
                        </div>
                      </div>
                      <Dumbbell className="h-4 w-4 text-primary flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="mine" className="mt-4">
            {filteredMine.length === 0 ? (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Dumbbell className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">A{'\u00fa'}n no has creado ejercicios propios</p>
                  <Button onClick={() => setShowCreate(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Crear ejercicio
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredMine.map(ex => (
                  <Card key={ex.id} className="bg-card border-blue-500/20" data-testid={`my-exercise-${ex.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground text-sm truncate">{ex.name}</p>
                            {ex.video_url && (
                              <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400 flex items-center gap-1">
                                <Video className="h-3 w-3" /> Video
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{ex.category}</Badge>
                            {ex.equipment && <span className="text-xs text-muted-foreground">{ex.equipment}</span>}
                          </div>
                          {uploadingId === ex.id && (
                            <div className="mt-2">
                              <Progress value={uploadProgress} className="h-1.5" />
                              <p className="text-[10px] text-muted-foreground mt-1">
                                {convertingVideo ? 'Convirtiendo a H.264...' : `Subiendo... ${uploadProgress}%`}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 ml-2">
                          <div className="flex gap-1">
                            {ex.video_url ? (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-400" onClick={() => openPlayer(ex.video_url)} title="Ver video" data-testid={`play-video-${ex.id}`}>
                                  <Play className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => triggerUploadFor(ex.id)} disabled={uploadingId !== null} title="Reemplazar video" data-testid={`replace-video-${ex.id}`}>
                                  <Upload className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-orange-400" onClick={() => handleDeleteVideo(ex.id)} disabled={uploadingId !== null} title="Borrar video" data-testid={`delete-video-${ex.id}`}>
                                  <Trash className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            ) : (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-400" onClick={() => triggerUploadFor(ex.id)} disabled={uploadingId !== null} title="Subir video" data-testid={`upload-video-${ex.id}`}>
                                <Upload className="h-3.5 w-3.5 mr-1" />
                                <span className="text-[10px]">Video</span>
                              </Button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowEdit({...ex})}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(ex.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle>Nuevo Ejercicio</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nombre *</Label><Input value={newExercise.name} onChange={(e) => setNewExercise(p => ({...p, name: e.target.value}))} className="bg-zinc-900/50 border-zinc-800" /></div>
              <div><Label>Categor{'\u00ed'}a</Label>
                <Select value={newExercise.category} onValueChange={(v) => setNewExercise(p => ({...p, category: v}))}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Grupo Muscular</Label><Input value={newExercise.muscle_group} onChange={(e) => setNewExercise(p => ({...p, muscle_group: e.target.value}))} placeholder="Ej: Pectoral mayor" className="bg-zinc-900/50 border-zinc-800" /></div>
              <div><Label>Equipamiento</Label><Input value={newExercise.equipment} onChange={(e) => setNewExercise(p => ({...p, equipment: e.target.value}))} placeholder="Ej: Mancuernas, Barra" className="bg-zinc-900/50 border-zinc-800" /></div>
              <div><Label>Notas T{'\u00e9'}cnicas</Label><Input value={newExercise.technical_notes} onChange={(e) => setNewExercise(p => ({...p, technical_notes: e.target.value}))} placeholder="Detalles de ejecuci\u00f3n" className="bg-zinc-900/50 border-zinc-800" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle>Editar Ejercicio</DialogTitle></DialogHeader>
            {showEdit && (
              <div className="space-y-4">
                <div><Label>Nombre</Label><Input value={showEdit.name} onChange={(e) => setShowEdit(p => ({...p, name: e.target.value}))} className="bg-zinc-900/50 border-zinc-800" /></div>
                <div><Label>Categor{'\u00ed'}a</Label>
                  <Select value={showEdit.category} onValueChange={(v) => setShowEdit(p => ({...p, category: v}))}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Grupo Muscular</Label><Input value={showEdit.muscle_group || ''} onChange={(e) => setShowEdit(p => ({...p, muscle_group: e.target.value}))} className="bg-zinc-900/50 border-zinc-800" /></div>
                <div><Label>Equipamiento</Label><Input value={showEdit.equipment || ''} onChange={(e) => setShowEdit(p => ({...p, equipment: e.target.value}))} className="bg-zinc-900/50 border-zinc-800" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(null)}>Cancelar</Button>
              <Button onClick={handleUpdate}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Hidden file input para video upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,.mp4,.mov,.m4v,.webm"
          className="hidden"
          onChange={onFileSelected}
          data-testid="trainer-video-input"
        />

        {/* Video Player Dialog */}
        <Dialog open={playerOpen} onOpenChange={(o) => { setPlayerOpen(o); if (!o) setPlayerUrl(null); }}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl">
            <DialogHeader><DialogTitle>Vista previa del video</DialogTitle></DialogHeader>
            {playerUrl && (
              <video
                key={playerUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-md bg-black"
                style={{ maxHeight: '70vh' }}
              >
                <source src={playerUrl} type="video/mp4" />
                Tu navegador no soporta la reproducción de este video.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TrainerLayout>
  );
};

export default TrainerExercisesPage;
